import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { useAppContext } from '../../../../../context/AppContext';
import { updateFormTemplateWorkflow, setFormTemplatesError } from '../../redux/actions/formTemplateApiActions';
import { fetchWorkflowDefinitions } from '../../redux/actions/workflowDefinitionApiActions';
import staticText from '../../staticText.json';
import { handleFormTemplateFailure, handleFormTemplateSuccess } from '../../utils/formTemplateNotifications';
import FormTemplateFooter from '../presentational/formTemplate/FormTemplateFooter/FormTemplateFooter';
import UpdateConfirmation from '../presentational/UpdateConfirmation/UpdateConfirmation';
import FormTemplateWorkflowList from './FormTemplateWorkflowList';

export function FormTemplateWorkflowsContainer(props) {
  const {
    accountId = undefined,
    planOptions = undefined,
    formTemplate = undefined,
    workflowDefinitions,
    workflowDefinitionRequestError = undefined,
    workflowDefinitionRequestLoading,
    formTemplateRequestType = null,
    fetchWorkflowDefinitionsConnect,
    updateFormTemplateWorkflowConnect,
    setFormTemplatesErrorConnect,
  } = props;

  const { notificationDomRef } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWorkflowDefinitionId, setSelectedWorkflowDefinitionId] = useState();
  const [showModal, setShowModal] = useState(false);
  const [reasonForChange, setReasonForChange] = useState();

  useEffect(() => {
    if (accountId !== undefined) {
      fetchWorkflowDefinitionsConnect();
    }
  }, [accountId]);

  useEffect(() => {
    return () => {
      if (formTemplateRequestType) {
        setFormTemplatesErrorConnect({}, null);
      }
    };
  }, [formTemplateRequestType]);

  useEffect(() => {
    if (formTemplate) {
      setSelectedWorkflowDefinitionId(formTemplate.workflow_definition_id);
    }
  }, [formTemplate]);

  const doFormsExist = () => {
    return formTemplate?.number_of_forms > 0;
  };

  const selectedWorkflowDefinitionUnchanged = () => {
    return formTemplate && selectedWorkflowDefinitionId === formTemplate.workflow_definition_id;
  };

  const isSaveButtonDisabled = () => {
    return isLoading || workflowDefinitions.length === 0 || doFormsExist() || selectedWorkflowDefinitionUnchanged();
  };

  const handleSave = async () => {
    if (isSaveButtonDisabled()) {
      return;
    }

    setShowModal(true);
  };

  const handleApiSave = async () => {
    setIsLoading(true);

    try {
      await updateFormTemplateWorkflowConnect(formTemplate.id, selectedWorkflowDefinitionId, reasonForChange);
      handleFormTemplateSuccess(notificationDomRef, setFormTemplatesErrorConnect, 'saveWorkflow');
    } catch (error) {
      handleFormTemplateFailure(error, notificationDomRef, setFormTemplatesErrorConnect, 'saveWorkflow');
    } finally {
      setIsLoading(false);
      setShowModal(false);
    }
  };

  const onSelectHandler = (workflowDefinitionId) => {
    setSelectedWorkflowDefinitionId(workflowDefinitionId);
  };

  const onReasonChangeHandler = (event) => {
    setReasonForChange(event.target.value);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const saveButtonProps = {
    handleFormAction: handleSave,
    isDisable: isSaveButtonDisabled(),
    showLoadingIcon: isLoading,
    buttonTitle: staticText.buttons.update_form,
  };

  return (
    <>
      <FormTemplateWorkflowList
        workflowDefinitions={workflowDefinitions}
        selectedWorkflowDefinitionId={selectedWorkflowDefinitionId}
        formsExist={doFormsExist()}
        signaturesEnabled={planOptions?.signatures_enabled}
        requestLoading={workflowDefinitionRequestLoading}
        requestError={workflowDefinitionRequestError}
        onSelect={onSelectHandler}
      />
      <FormTemplateFooter saveButton={saveButtonProps} />
      <UpdateConfirmation
        showModal={showModal}
        handleChangeReason={onReasonChangeHandler}
        changeReasonValue={reasonForChange}
        closeModal={closeModal}
        handleSave={handleApiSave}
        showLoadingIcon={isLoading}
      />
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    accountId: state.internalReducer.internal.accountId,
    planOptions: state.internalReducer.internal.planOptions,
    formTemplate: state.formTemplateReducer.formTemplate,
    workflowDefinitions: state.workflowDefinitionApiReducer.workflowDefinitions,
    workflowDefinitionRequestError: state.workflowDefinitionApiReducer.workflowDefinitionRequest.fetchRequest.error,
    workflowDefinitionRequestLoading: state.workflowDefinitionApiReducer.workflowDefinitionRequest.fetchRequest.loading,
    formTemplateRequestType: state.formTemplateApiReducer.formTemplateRequest.requestType,
  };
};

const mapDispatchToProps = {
  fetchWorkflowDefinitionsConnect: fetchWorkflowDefinitions,
  updateFormTemplateWorkflowConnect: updateFormTemplateWorkflow,
  setFormTemplatesErrorConnect: setFormTemplatesError,
};

FormTemplateWorkflowsContainer.propTypes = {
  accountId: PropTypes.number,
  planOptions: PropTypes.shape({
    signatures_enabled: PropTypes.bool,
  }),
  formTemplate: PropTypes.shape({
    id: PropTypes.number,
    number_of_forms: PropTypes.number,
    workflow_definition_id: PropTypes.number,
  }),
  workflowDefinitions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }),
  ).isRequired,
  workflowDefinitionRequestError: PropTypes.shape({
    name: PropTypes.string,
    message: PropTypes.string,
  }),
  workflowDefinitionRequestLoading: PropTypes.bool.isRequired,
  formTemplateRequestType: PropTypes.string,
  fetchWorkflowDefinitionsConnect: PropTypes.func.isRequired,
  updateFormTemplateWorkflowConnect: PropTypes.func.isRequired,
  setFormTemplatesErrorConnect: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormTemplateWorkflowsContainer);
